import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HKPImage } from "../../../media/HKPImage";
import ProductImagePreView from "../../products/ProductImagePreView";
import { pickProductListPreviewUrls } from "../../products/previewMedia";
import { rupees } from "../rates/rateModel";
import { useKT } from "../i18n/kt";
import { withReturnMarker } from "../ui/leaveKarkhana";
import { kp } from "../karkhanaPaths";

/** Small thumbnails, same size as the product list; full pictures load only when one is opened. */
export const ProductThumbs = ({ media, max = 4, size = 44 }: { media?: any[]; max?: number; size?: number }) => {
  const { t } = useKT();
  const [open, setOpen] = useState<string[] | null>(null);
  const urls = pickProductListPreviewUrls(media);
  if (!urls.length) return null;
  const show = urls.slice(0, max);
  return (
    <span className="kc-thumbs">
      {show.map((u, i) => (
        <button key={u} type="button" className="kc-thumb" style={{ width: size, height: size }} aria-label={t("Open picture {{n}}", { n: i + 1 })}
          onClick={() => setOpen([...urls.slice(i), ...urls.slice(0, i)])}>
          <HKPImage preset="THUMBNAIL" legacyUrl={u} alt="" />
          {i === show.length - 1 && urls.length > max ? <span className="kc-thumb__more">+{urls.length - max}</span> : null}
        </button>
      ))}
      {open ? <ProductImagePreView openModal={!!open} closeModal={() => setOpen(null)} images={open} /> : null}
    </span>
  );
};

/**
 * The saree's product with its pictures, and its variants (same product family). On approval every
 * variant gets this costing's prices unless it is ticked to keep its own, or it is another saree
 * number with its own approved costing.
 */
const CostingProductPanel = ({
  product,
  variants,
  keepOwn,
  canEdit,
  onKeepOwn,
  onOpenProduct,
  onStartCosting,
}: {
  product?: any;
  variants?: any[];
  keepOwn: string[];
  canEdit: boolean;
  onKeepOwn: (ids: string[]) => void;
  /** Called before going to a product page, so its back button returns here. */
  onOpenProduct?: () => void;
  /** A variant that is its own saree number: start a separate costing for it. */
  onStartCosting?: (pieceId: string) => void;
}) => {
  const { t } = useKT();
  if (!product) return null;
  const list = variants || [];
  const hasPictures = pickProductListPreviewUrls(product.media).length > 0;
  return (
    <section className="kc-card kc-product">
      <div className="kc-product__main">
        <ProductThumbs media={product.media} max={5} />
        <div>
          <Link to={withReturnMarker(`/product/${encodeURIComponent(product.productUniqueId)}`)} onClick={onOpenProduct}>
            <strong>{product.name || product.productUniqueId}</strong>
          </Link>
          <small>
            {[product.color, product.srNo].filter(Boolean).join(" · ")}
            {product.sellPrice ? ` · ${t("now")} ${rupees(product.sellPrice, 0)}` : ""}
          </small>
          {!hasPictures ? (
            <small>
              {t("No pictures yet.")} <Link to={withReturnMarker(`/edit-product/${encodeURIComponent(product.productUniqueId)}`)} onClick={onOpenProduct}>{t("Add pictures to the product")}</Link>
            </small>
          ) : null}
        </div>
      </div>
      {list.length ? (
        <div className="kc-variants">
          <span className="kc-muted">
            {t("Variants of this saree get the same cost and sale price when approved, unless ticked to keep their own.")}
          </span>
          {list.map((v) => {
            const own = !!v.ownCosting;
            const kept = keepOwn.includes(v.productUniqueId);
            return (
              <div key={v.productUniqueId} className="kc-variant">
                <span className="kc-variant__pic">
                  <ProductThumbs media={v.media} max={1} size={36} />
                </span>
                <Link to={withReturnMarker(`/product/${encodeURIComponent(v.productUniqueId)}`)} className="kc-variant__name" onClick={onOpenProduct}>
                  {v.color || v.name || v.productUniqueId}
                  <small>{v.srNo || v.productUniqueId}</small>
                </Link>
                <span className="kc-muted">{v.sellPrice ? rupees(v.sellPrice, 0) : "—"}</span>
                {v.ownCostingId ? (
                  <Link to={kp.costing(v.ownCostingId)} className="kc-variant__costing">
                    {own ? t("Saree {{n}}: its own costing", { n: v.ownSareeNumber }) : t("Saree {{n}}: open its costing", { n: v.ownSareeNumber })}
                  </Link>
                ) : v.ownPieceId && onStartCosting ? (
                  <button type="button" className="kc-addlink" onClick={() => onStartCosting(v.ownPieceId)}>
                    {t("Saree {{n}}: start its own costing", { n: v.ownSareeNumber })}
                  </button>
                ) : null}
                {own ? null : (
                  <label className="kc-keep">
                    <input
                      type="checkbox"
                      checked={kept}
                      disabled={!canEdit}
                      onChange={(e) => onKeepOwn(e.target.checked ? [...keepOwn, v.productUniqueId] : keepOwn.filter((x) => x !== v.productUniqueId))}
                    />
                    {t("Keep its own price")}
                  </label>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default CostingProductPanel;
